// Фон игры: каньон с железнодорожным путём, гора слева и станция справа.
//
// Фон нужен и экрану интро, и игровому полю, поэтому он живёт отдельно от них.
//
// Рисует его скелет, а не картинка: тот же фон умеет ехать, см. ADR-51. В покое он показывает
// картинку станции одним куском, а на разгоне уводит гору со станцией вдаль, подменяет картинку
// размытой и гонит землю мимо машины. Поездку ведёт ui/ride.ts: здесь только сам показ.
//
// Больших картинок в атласе скелета нет — их надевает на его сетки менеджер ассетов,
// см. BACKGROUND_SKELETON.

import { Container, Rectangle } from 'pixi.js';

import { BACKGROUND_PLATE, BACKGROUND_SKELETON, BACKGROUND_SWAP } from '../assets/catalog';
import type { AssetManager, Slot, Spine, TrackEntry } from '../assets/manager';

import { animationSeconds, type Viewport } from './scene';

/**
 * Во сколько раз поездка идёт быстрее, чем нарисована.
 *
 * Художник нарисовал разгон на двенадцать секунд. Бонус за это время успевает объявиться,
 * подвести взгляд к мини-слоту и крутнуть его, поэтому полного хода игрок не видел вовсе:
 * поезд ещё разгонялся, а бонус уже кончился.
 *
 * Вдвое быстрее разгон укладывается в шесть секунд и кончается как раз к спину мини-слота.
 *
 * Быстрее идёт вся поездка, а не один разгон. Разгон кончается той скоростью, с какой идёт
 * круг ровного хода, и ускорь его одного — земля тормозила бы ровно там, где поезд разогнался.
 */
const RIDE_SPEED: number = 2;

/**
 * За сколько ход фона переходит в прибытие.
 *
 * Прочие ходы стыкуются сами: разгон кончается той же позой, какой кончается круг ровного хода,
 * а прибытие — той, с какой начинается покой. Такие стыки играются встык, без перехода: смешай
 * их — и гора поехала бы назад посреди кадра.
 *
 * Стык один и не сходится: прибытие начинается с начала круга, а застаёт его где придётся.
 * Его и сглаживает переход.
 */
const SWAP_SECONDS: number = 0.3;

/** Фон, закрывающий макет вместе с полями по краям. */
export class Background {
  readonly view: Container = new Container();

  readonly #spine: Spine;

  constructor(assets: AssetManager) {
    const { still, depart, run, arrive } = BACKGROUND_SKELETON.animations;

    this.#spine = assets.spine(BACKGROUND_SKELETON);

    // Кадры фон получает от сцены, а не от общего тикера: подмена доводится сразу
    // за анимацией, и порядок этих двух шагов должен быть виден в коде, см. #settle.
    this.#spine.autoUpdate = false;

    // Прибытие застаёт ход где придётся, поэтому к нему фон переходит внахлёст. Оборвать его
    // может и разгон: игрок вправе оборвать бонус, не дождавшись даже полного хода.
    this.#spine.state.data.setMix(run, arrive, SWAP_SECONDS);
    this.#spine.state.data.setMix(depart, arrive, SWAP_SECONDS);

    this.view.addChild(this.#spine);
    this.#spine.state.setAnimation(0, still, true);
  }

  /** Двигает фон и доводит подмену нарисованного спрайтами. */
  update(seconds: number): void {
    this.#spine.update(seconds);
    this.#settle();
  }

  /**
   * Растягивает фон на всю видимую область.
   *
   * Фон стоит в центре макета и вылезает за его края: пустых полей на экране быть не должно.
   * Ось стоит не по центру картинки, поэтому до ближнего края от неё меньше половины ширины.
   * Закрыть надо именно эту короткую сторону, иначе у края экрана останется пустая полоса.
   *
   * Габарит скелета в счёт не идёт: он снят по всей поездке и тянется на два десятка экранов.
   * Вписывается в экран сетка стоящего фона, а всё остальное лежит за его краями.
   */
  layout(viewport: Viewport): void {
    const { left, right, bottom, top, axisX } = BACKGROUND_PLATE;

    const width: number = right - left;
    const height: number = top - bottom;
    const axis: number = (axisX - left) / width;
    const nearestEdge: number = Math.min(axis, 1 - axis);

    const scale: number = Math.max(viewport.width / (2 * width * nearestEdge), viewport.height / height);

    // Ось Y у Spine смотрит вверх, а у Pixi вниз, поэтому середина картинки прибавляется.
    this.#spine.scale.set(scale);
    this.#spine.position.set(-axisX * scale, ((top + bottom) / 2) * scale);

    // Нажатие ловит весь экран, см. scenes/introScene.ts. Габарит скелета для этого не годится:
    // он куда шире экрана, и клик по нему ловился бы и за краем видимой области.
    this.view.hitArea = new Rectangle(-viewport.width / 2, -viewport.height / 2, viewport.width, viewport.height);
  }

  /** Сколько идёт разгон на экране. До его конца поезд полным ходом ещё не идёт. */
  get departSeconds(): number {
    return animationSeconds(this.#spine, BACKGROUND_SKELETON.animations.depart) / RIDE_SPEED;
  }

  /**
   * Отправляет поезд: гора со станцией уходят вдаль, а земля идёт мимо машины.
   *
   * Разгон кончается сам и переходит в ровный ход, поэтому ждать его некому: поездка идёт,
   * пока её не остановит прибытие.
   */
  depart(): void {
    const { depart, run } = BACKGROUND_SKELETON.animations;

    this.#spine.state.setAnimation(0, depart, false).timeScale = RIDE_SPEED;
    this.#spine.state.addAnimation(0, run, true, 0).timeScale = RIDE_SPEED;
  }

  /**
   * Приводит поезд к станции и ждёт, пока он встанет.
   *
   * Ожидание кончается и тогда, когда прибытие сняли с дорожки: поезд, которому велели ехать
   * заново, до станции уже не доедет, а брошенное ожидание не кончилось бы никогда.
   */
  arrive(): Promise<void> {
    const { arrive, still } = BACKGROUND_SKELETON.animations;

    const track: TrackEntry = this.#spine.state.setAnimation(0, arrive, false);
    track.timeScale = RIDE_SPEED;

    // Покой идёт своим ходом: ускорена сама поездка, а не облака над стоящей машиной.
    this.#spine.state.addAnimation(0, still, true, 0);

    return new Promise<void>((resolve) => {
      track.listener = { complete: (): void => resolve(), end: (): void => resolve() };
    });
  }

  /**
   * Держит нарисованную плиту, пока спрайты не доехали.
   *
   * Гору художник свёл почти верно: её ход кончается на 5.3 секунде из 6.7, а тает она с 6.0,
   * то есть на своё место уже пришла. Сетку он доводит до последнего кадра, но за время ухода
   * она добирает десяток пикселей, и встаёт спрайт мимо нарисованной горы на два десятка.
   * Убрать это расхождение сроками нельзя: оно лежит в самой анимации.
   *
   * Плиту он вывел раньше — она встаёт непрозрачной за полсекунды до конца хода, а спрайт
   * станции в эти полсекунды ещё крупнее нарисованной, и станций на экране две.
   *
   * Ждёт поэтому плита, а не станция: своди её спрайт, и станция растворилась бы на ходу,
   * не доехав, а нарисованная встала бы вместо неё на своём месте — прыжком.
   *
   * Обе стороны подмены идут теперь по одной кривой — по горе, и сходятся к концу хода,
   * где стоят уже все спрайты. Размытая плита под нарисованной держится непрозрачной, пока
   * та не встала: своим ходом она тает раньше, и сквозь обе светила бы пустота.
   *
   * Считается это после анимации: цвета слотов она задаёт каждый кадр заново.
   */
  #settle(): void {
    const track: TrackEntry | null = this.#spine.state.getCurrent(0);
    if (track?.animation?.name !== BACKGROUND_SKELETON.animations.arrive)
      return;

    const plate: Slot | null = this.#spine.skeleton.findSlot(BACKGROUND_SWAP.plate);
    const blur: Slot | null = this.#spine.skeleton.findSlot(BACKGROUND_SWAP.blur);
    const mountain: Slot | null = this.#spine.skeleton.findSlot(BACKGROUND_SWAP.mountain);

    if (plate === null || blur === null || mountain === null)
      return;

    plate.color.a = 1 - mountain.color.a;

    if (plate.color.a < 1)
      blur.color.a = 1;
  }
}
